import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { CompanyProfile, CompanyStore } from '@/lib/company-store';

interface CompanyProfileModalProps {
  company: CompanyProfile;
  onClose: () => void;
  onSave: (company: CompanyProfile) => void;
}

export const CompanyProfileModal = ({ company, onClose, onSave }: CompanyProfileModalProps) => {
  const [formData, setFormData] = useState(company);
  const [logoPreview, setLogoPreview] = useState(company.logo);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setFormData(prev => ({ ...prev, logo: base64 }));
        setLogoPreview(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    CompanyStore.saveCompany(formData);
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex justify-between items-center sticky top-0 bg-white border-b">
          <CardTitle>Sunting Profil Perusahaan</CardTitle>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded">
            <X size={20} />
          </button>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Logo Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-700">Logo Syarikat</label>
              <div className="flex items-center gap-4">
                {logoPreview && (
                  <div className="w-24 h-24 border border-slate-300 rounded-lg flex items-center justify-center overflow-hidden bg-slate-50">
                    <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
                />
              </div>
            </div>

            {/* Company Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Nama Syarikat</label>
                <Input
                  type="text"
                  name="nama"
                  value={formData.nama}
                  onChange={handleChange}
                  placeholder="SAP Ngecat"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">No. Pendaftaran</label>
                <Input
                  type="text"
                  name="noPendaftaran"
                  value={formData.noPendaftaran}
                  onChange={handleChange}
                  placeholder="SA0601971-K"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Nama Pemilik</label>
                <Input
                  type="text"
                  name="namaOwner"
                  value={formData.namaOwner}
                  onChange={handleChange}
                  placeholder="Nama Pemilik"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">No. Telefon</label>
                <Input
                  type="tel"
                  name="noTel"
                  value={formData.noTel}
                  onChange={handleChange}
                  placeholder="03-1234-5678"
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Emel</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="info@sapngecat.com"
                  required
                />
              </div>
            </div>

            {/* Address */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Alamat</label>
              <textarea
                name="alamat"
                value={formData.alamat}
                onChange={handleChange}
                placeholder="Jalan Perdagangan No. 123, Kuala Lumpur"
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                required
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-6 border-t">
              <Button type="submit" className="bg-red-600 hover:bg-red-700 flex-1">
                Simpan Perubahan
              </Button>
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Tutup
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};